import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle, XCircle, Archive, Clock, Image, Eye, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminListings = () => {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, approved, all
  const [showDenyModal, setShowDenyModal] = useState(null);
  const [denyComments, setDenyComments] = useState('');
  const [selectedListing, setSelectedListing] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchListings();
  }, [isAdmin, navigate, filter]);

  const fetchListings = async () => {
    try {
      const response = await fetch(`${API}/api/marketplace/listings?status=${filter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setListings(data);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (listingId) => {
    try {
      const response = await fetch(`${API}/api/admin/listings/${listingId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Listing approved!');
        fetchListings();
      }
    } catch (err) {
      toast.error('Failed to approve listing');
    }
  };

  const handleDeny = async () => {
    if (!showDenyModal) return;
    try {
      const response = await fetch(`${API}/api/admin/listings/${showDenyModal}/deny`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ comments: denyComments })
      });
      if (response.ok) {
        toast.success('Listing denied');
        setShowDenyModal(null);
        setDenyComments('');
        fetchListings();
      }
    } catch (err) {
      toast.error('Failed to deny listing');
    }
  };

  const handleArchive = async (listingId) => {
    try {
      const response = await fetch(`${API}/api/admin/listings/${listingId}/archive`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Listing archived');
        fetchListings();
      }
    } catch (err) {
      toast.error('Failed to archive listing');
    }
  };

  const handleArchiveExpired = async () => {
    try {
      const response = await fetch(`${API}/api/admin/archive-expired`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Archived ${data.archived_count} expired listings`);
        fetchListings();
      }
    } catch (err) {
      toast.error('Failed to archive expired listings');
    }
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY delete this listing? This cannot be undone.')) return;
    try {
      const response = await fetch(`${API}/api/marketplace/listings/${listingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Listing permanently deleted');
        fetchListings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete listing');
      }
    } catch (err) {
      toast.error('Failed to delete listing');
    }
  };

  const handleReinstate = async (listingId) => {
    try {
      const response = await fetch(`${API}/api/marketplace/listings/${listingId}/reinstate`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Listing reinstated and set to pending approval');
        fetchListings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reinstate listing');
      }
    } catch (err) {
      toast.error('Failed to reinstate listing');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
      approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
      denied: { bg: 'bg-red-100', text: 'text-red-700', label: 'Denied' },
      sold: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Sold' },
      archived: { bg: 'bg-stone-100', text: 'text-stone-700', label: 'Archived' },
      hidden: { bg: 'bg-stone-100', text: 'text-stone-700', label: 'Hidden' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const badges = {
      sell: '🏷️ For Sale',
      buy: '🔍 Wanted',
      gift: '🎁 Gift',
      handyman: '🔧 Handyman'
    };
    return badges[type] || badges.sell;
  };

  const getDaysRemaining = (expiresAt) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const days = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  if (loading) {
    return <div className="flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="bg-stone-50 p-4">
      <div className="max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-amber-600" />
              Review Listings
            </h1>
          </div>
          <Button variant="outline" onClick={handleArchiveExpired}>
            <Archive className="w-4 h-4 mr-2" /> Archive Expired
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-4 mb-6">
          <div className="flex gap-2">
            {[
              { value: 'pending', label: 'Pending', icon: Clock },
              { value: 'approved', label: 'Approved', icon: CheckCircle },
              { value: 'all', label: 'All Listings', icon: Eye }
            ].map(f => (
              <Button
                key={f.value}
                variant={filter === f.value ? 'default' : 'outline'}
                onClick={() => { setFilter(f.value); setLoading(true); }}
                size="sm"
              >
                <f.icon className="w-4 h-4 mr-1" /> {f.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Listings Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden">
          <div className="p-4 border-b border-stone-200 bg-stone-50">
            <h2 className="font-semibold text-stone-800">{listings.length} Listings</h2>
          </div>

          {listings.length === 0 ? (
            <div className="p-12 text-center text-stone-500">
              <Clock className="w-12 h-12 mx-auto mb-4 text-stone-300" />
              <p>No {filter} listings</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {listings.map(listing => (
                <div key={listing.id} className="p-4 hover:bg-stone-50">
                  <div className="flex gap-4">
                    {/* Image */}
                    <div className="w-24 h-24 bg-stone-100 rounded-xl overflow-hidden flex-shrink-0">
                      {listing.images && listing.images.length > 0 ? (
                        <img
                          src={`${API}/api/uploads/${listing.images[0]}`}
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-stone-300">
                          <Image className="w-8 h-8" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-bold text-stone-800">{listing.title}</h3>
                          <p className="text-sm text-stone-500 line-clamp-2">{listing.description}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {listing.listing_type !== 'gift' && listing.price > 0 ? (
                            <span className="text-lg font-bold text-emerald-600">${listing.price}</span>
                          ) : (
                            <span className="text-lg font-bold text-purple-600">Free</span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 mt-2 text-sm">
                        <span className="text-stone-400">{getTypeBadge(listing.listing_type)}</span>
                        <span className="text-stone-300">•</span>
                        {getStatusBadge(listing.status)}
                        <span className="text-stone-300">•</span>
                        <span className="text-stone-500">By {listing.user_name || listing.user_email}</span>
                        <span className="text-stone-300">•</span>
                        <span className="text-stone-500">
                          {getDaysRemaining(listing.expires_at) > 0 
                            ? `${getDaysRemaining(listing.expires_at)} days left`
                            : 'Expired'}
                        </span>
                      </div>

                      {/* View Images */}
                      {listing.images && listing.images.length > 1 && (
                        <button
                          onClick={() => setSelectedListing(listing)}
                          className="text-sm text-amber-600 hover:text-amber-700 mt-2"
                        >
                          View all {listing.images.length} images
                        </button>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {listing.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(listing.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowDenyModal(listing.id)}
                            className="border-red-300 text-red-600 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Deny
                          </Button>
                        </>
                      )}
                      {listing.status === 'approved' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleArchive(listing.id)}
                        >
                          <Archive className="w-4 h-4 mr-1" /> Hide
                        </Button>
                      )}
                      {['archived', 'hidden', 'denied'].includes(listing.status) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReinstate(listing.id)}
                          className="border-green-300 text-green-600 hover:bg-green-50"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" /> Reinstate
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(listing.id)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Deny Modal */}
        {showDenyModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-stone-800 mb-4">Deny Listing</h3>
              <p className="text-stone-600 mb-4">Provide a reason for denial (optional but recommended):</p>
              <textarea
                value={denyComments}
                onChange={e => setDenyComments(e.target.value)}
                placeholder="e.g., Listing violates community guidelines..."
                className="w-full p-3 border border-stone-200 rounded-xl resize-none h-24 mb-4"
              />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setShowDenyModal(null); setDenyComments(''); }} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleDeny} className="flex-1 bg-red-600 hover:bg-red-700">
                  Deny Listing
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Image Gallery Modal */}
        {selectedListing && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedListing(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-stone-800">{selectedListing.title}</h3>
                <button onClick={() => setSelectedListing(null)} className="p-2 hover:bg-stone-100 rounded-lg">
                  ✕
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {selectedListing.images.map((img, index) => (
                  <img
                    key={index}
                    src={`${API}/api/uploads/${img}`}
                    alt={`${selectedListing.title} ${index + 1}`}
                    className="w-full h-48 object-cover rounded-xl"
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminListings;
